import React, { useState, useEffect } from 'react'
import { supabase, SUPABASE_URL } from './supabaseClient'
import { useAuth } from './AuthContext'
import { verificarNIF } from './nifUtils'
import { Mail, Lock, AlertCircle, Eye, EyeOff, UserPlus, User, Hash, CheckCircle, Loader, Phone, MapPin, KeyRound, ArrowLeft, Building2, ArrowRight } from 'lucide-react'
import PwaInstallBanner from './PwaInstallBanner'

// Paleta LogNow Store - Dark + Yellow editorial
const T = {
  black: '#000000',
  bgDark: '#020202',
  zinc950: '#09090b',
  zinc900: '#18181b',
  zinc800: '#27272a',
  yellow: '#FACC15',
  yellow50: '#FEF9C3',
  yellow700: '#A16207',
  white: '#FFFFFF',
  white70: 'rgba(255,255,255,0.70)',
  white40: 'rgba(255,255,255,0.40)',
  white10: 'rgba(255,255,255,0.10)',
  white05: 'rgba(255,255,255,0.05)',
  success: '#16A34A',
  danger: '#EF4444',
  warning: '#F59E0B',
}

const LOGO_CLARO = '/logo-claro.png' // logo sobre fundo escuro

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/
function validarEmail(s) {
  const v = (s || '').trim()
  if (!v) return 'empty'
  return EMAIL_RE.test(v) ? 'valid' : 'invalid'
}

export default function Login() {
  const { signIn, inPasswordRecovery, clearPasswordRecovery } = useAuth()
  const [modo, setModo] = useState(inPasswordRecovery ? 'definir_nova' : 'login')

  // Login
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [showPass, setShowPass] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  // Recuperar password
  const [recEmail, setRecEmail] = useState('')
  const [recLoading, setRecLoading] = useState(false)
  const [recError, setRecError] = useState('')

  // Definir nova password
  const [novaPass, setNovaPass] = useState('')
  const [novaPass2, setNovaPass2] = useState('')
  const [novaPassShow, setNovaPassShow] = useState(false)
  const [novaLoading, setNovaLoading] = useState(false)
  const [novaError, setNovaError] = useState('')
  const [novaOk, setNovaOk] = useState(false)

  // Registo
  const [rNome, setRNome] = useState('')
  const [rEmail, setREmail] = useState('')
  const [rNif, setRNif] = useState('')
  const [rTelefone, setRTelefone] = useState('')
  const [rMorada, setRMorada] = useState('')
  const [rPass, setRPass] = useState('')
  const [rShowPass, setRShowPass] = useState(false)
  const [rLoading, setRLoading] = useState(false)
  const [rError, setRError] = useState('')
  const [nifVerificando, setNifVerificando] = useState(false)
  const [nifTipo, setNifTipo] = useState(null)

  // Convite B2C (link de convite gerado pelo admin)
  const [conviteToken, setConviteToken] = useState(null)
  const [conviteEstado, setConviteEstado] = useState(null) // null | 'a_validar' | 'valido' | { erro }

  // Permitir scroll
  useEffect(() => {
    const html = document.documentElement
    const body = document.body
    const prev = {
      htmlO: html.style.overflow, htmlH: html.style.height,
      bodyO: body.style.overflow, bodyH: body.style.height,
    }
    html.style.overflow = 'auto'; html.style.height = 'auto'
    body.style.overflow = 'auto'; body.style.height = 'auto'
    const root = document.getElementById('root')
    const prevRoot = root ? { o: root.style.overflow, h: root.style.height } : null
    if (root) { root.style.overflow = 'visible'; root.style.height = 'auto' }
    return () => {
      html.style.overflow = prev.htmlO; html.style.height = prev.htmlH
      body.style.overflow = prev.bodyO; body.style.height = prev.bodyH
      if (root && prevRoot) { root.style.overflow = prevRoot.o; root.style.height = prevRoot.h }
    }
  }, [])

  useEffect(() => {
    if (inPasswordRecovery) setModo('definir_nova')
  }, [inPasswordRecovery])

  useEffect(() => {
    // Mensagem do gate de acesso (conta pendente, recusada, etc.)
    try {
      const gateMsg = window.localStorage.getItem('login_gate_msg')
      if (gateMsg) {
        setError(gateMsg)
        window.localStorage.removeItem('login_gate_msg')
      }
    } catch {}
    const urlParams = new URLSearchParams(window.location.search)
    const loginEmail = urlParams.get('login_email')
    if (loginEmail) {
      setEmail(loginEmail)
      window.history.replaceState({}, document.title, window.location.pathname)
    }
    const convite = urlParams.get('convite')
    if (convite) {
      setConviteToken(convite)
      setModo('registo_b2c')
      setConviteEstado('a_validar')
      ;(async () => {
        const { data, error } = await supabase.rpc('validar_convite_b2c', { p_token: convite })
        if (error || !data?.ok) {
          setConviteEstado({ erro: data?.erro || 'invalido' })
        } else {
          setConviteEstado('valido')
        }
      })()
    }
  }, [])

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!email || !password) { setError('Preenche todos os campos.'); return }
    setLoading(true); setError('')
    const { error: err } = await signIn(email, password)
    if (err) setError(err.message.includes('Invalid') ? 'Email ou password incorrectos.' : err.message)
    setLoading(false)
  }

  const handleRecuperar = async (e) => {
    e.preventDefault()
    const em = recEmail.trim()
    if (!em) { setRecError('Indica o teu email.'); return }
    setRecLoading(true); setRecError('')
    try {
      const { error: err } = await supabase.auth.resetPasswordForEmail(em, { redirectTo: window.location.origin })
      if (err) throw err
      setModo('recuperar_enviado')
    } catch (err) {
      setRecError('Erro: ' + (err?.message || String(err)))
    }
    setRecLoading(false)
  }

  const handleDefinirNova = async (e) => {
    e.preventDefault()
    if (novaPass.length < 8) { setNovaError('Password deve ter pelo menos 8 caracteres.'); return }
    if (novaPass !== novaPass2) { setNovaError('As passwords não coincidem.'); return }
    setNovaLoading(true); setNovaError('')
    try {
      const { error: err } = await supabase.auth.updateUser({ password: novaPass })
      if (err) throw err
      setNovaOk(true)
      if (typeof window !== 'undefined') window.history.replaceState({}, document.title, window.location.pathname)
      await supabase.auth.signOut()
      clearPasswordRecovery()
      setTimeout(() => {
        setNovaOk(false); setNovaPass(''); setNovaPass2('')
        setModo('login')
      }, 2500)
    } catch (err) {
      setNovaError('Erro: ' + (err?.message || String(err)))
    }
    setNovaLoading(false)
  }

  const handleNifBlur = async () => {
    const nif = rNif.trim().replace(/\s/g, '')
    if (nif.length < 9) { setNifTipo(null); return }
    setNifVerificando(true)
    const { tipo, dados } = await verificarNIF(nif)
    setNifTipo(tipo)
    setNifVerificando(false)
    // Auto-preencher campos com dados oficiais da AT (apenas se utilizador
    // ainda nao escreveu nada nesses campos — nao sobrepor input manual)
    if (dados) {
      if (!rNome.trim() && dados.nome) setRNome(dados.nome)
      if (!rMorada.trim()) {
        const partes = [dados.morada, dados.codPostal, dados.cidade].filter(Boolean)
        if (partes.length > 0) setRMorada(partes.join(', '))
      }
    }
  }

  const handleRegisto = async (e) => {
    e.preventDefault()
    const nif = rNif.trim().replace(/\s/g, '')
    if (!rNome.trim() || !rEmail.trim() || !nif || !rTelefone.trim() || !rMorada.trim() || !rPass) {
      setRError('Preenche todos os campos obrigatórios.'); return
    }
    if (nif.length < 9) { setRError('NIF inválido — deve ter 9 dígitos.'); return }
    if (rPass.length < 8) { setRError('Password deve ter pelo menos 8 caracteres.'); return }
    setRLoading(true); setRError('')
    try {
      let tipo = nifTipo
      if (!tipo) {
        const r = await verificarNIF(nif)
        tipo = r.tipo
      }
      if (tipo === 'invalido') {
        setRError('NIF inválido — verifica os 9 dígitos. Se está correcto, contacta-nos para registo manual.')
        setRLoading(false); return
      }
      if (tipo === 'desconhecido') {
        setRError('Não conseguimos classificar automaticamente este NIF (prefixo 7/9). Contacta-nos para registo manual.')
        setRLoading(false); return
      }
      // Mapeamento NIF -> tipo_cliente: particular vira B2C, restantes (ENI, empresa) B2B
      const tipoClienteFinal = tipo === 'particular' ? 'particular' : 'b2b'
      // Todos os dados vao via raw_user_meta_data — o trigger handle_new_user
      // (SECURITY DEFINER) cria/actualiza o perfil automaticamente sem precisar
      // de RLS, evitando o problema de "Confirm Email" on (sem session post-signUp).
      const { data, error: authErr } = await supabase.auth.signUp({
        email: rEmail.trim(),
        password: rPass,
        options: {
          // Garante que o link de confirmacao redireciona para o site actual
          // (mesmo que Site URL do Supabase esteja mal configurado).
          emailRedirectTo: `${window.location.origin}/login`,
          data: {
            nome: rNome.trim(),
            role: 'cliente',
            nif,
            tipo_nif: tipo,
            tipo_cliente: tipoClienteFinal,
            telefone: rTelefone.trim(),
            morada: rMorada.trim(),
          },
        },
      })
      if (authErr) throw new Error(authErr.message)
      const uid = data?.user?.id
      if (!uid) throw new Error('Erro ao criar conta.')
      setModo('pendente_email')
    } catch (err) {
      setRError(err.message.includes('already') ? 'Este email já está registado.' : 'Erro: ' + err.message)
    }
    setRLoading(false)
  }

  // Registo B2C via link de convite (sem validação NIF, particular forçado)
  const handleRegistoB2C = async (e) => {
    e.preventDefault()
    if (!rNome.trim() || !rEmail.trim() || !rPass) {
      setRError('Nome, email e password são obrigatórios.'); return
    }
    if (rPass.length < 8) { setRError('Password deve ter pelo menos 8 caracteres.'); return }
    if (!conviteToken || conviteEstado !== 'valido') {
      setRError('Link de convite inválido ou expirado.'); return
    }
    const nif = rNif.trim().replace(/\s/g, '')
    setRLoading(true); setRError('')
    try {
      const { data, error: authErr } = await supabase.auth.signUp({
        email: rEmail.trim(),
        password: rPass,
        options: {
          emailRedirectTo: `${window.location.origin}/login`,
          data: {
            nome: rNome.trim(),
            role: 'cliente',
            nif: nif || null,
            tipo_nif: 'particular',
            tipo_cliente: 'particular',
            telefone: rTelefone.trim(),
            morada: rMorada.trim(),
          },
        },
      })
      if (authErr) throw new Error(authErr.message)
      const uid = data?.user?.id
      if (!uid) throw new Error('Erro ao criar conta.')
      // Tenta consumir o token (precisa de sessão; se confirm email on não há, mas o trigger já criou perfil como particular)
      if (data?.session) {
        await supabase.rpc('consumir_convite_b2c', { p_token: conviteToken })
      } else {
        // Sem sessão: login temporário para marcar o token usado, depois logout
        const { data: si } = await supabase.auth.signInWithPassword({ email: rEmail.trim(), password: rPass })
        if (si?.session) {
          await supabase.rpc('consumir_convite_b2c', { p_token: conviteToken })
          await supabase.auth.signOut()
        }
      }
      setModo('pendente_email')
    } catch (err) {
      setRError(err.message.includes('already') ? 'Este email já está registado.' : 'Erro: ' + err.message)
    }
    setRLoading(false)
  }

  // ─────────── Render ───────────
  return (
    <div style={{
      minHeight: '100vh', background: T.black, color: T.white,
      fontFamily: "Inter, 'Segoe UI', system-ui, -apple-system, sans-serif",
      WebkitFontSmoothing: 'antialiased',
      display: 'flex', flexDirection: 'column', position: 'relative', overflow: 'hidden',
    }}>
      {/* Background pattern (subtle radial yellow + grid) */}
      <div aria-hidden style={{
        position: 'absolute', inset: 0, pointerEvents: 'none',
        background: `radial-gradient(ellipse 700px 500px at 80% 20%, rgba(250,204,21,0.08) 0%, transparent 60%), ${T.bgDark}`,
      }} />
      <div aria-hidden style={{
        position: 'absolute', inset: 0, pointerEvents: 'none',
        backgroundImage: 'linear-gradient(to right, rgba(255,255,255,0.025) 1px, transparent 1px), linear-gradient(to bottom, rgba(255,255,255,0.025) 1px, transparent 1px)',
        backgroundSize: '80px 80px',
      }} />

      {/* Header */}
      <header style={{
        position: 'relative', zIndex: 2, padding: 'clamp(14px, 3vw, 22px) clamp(16px, 4vw, 32px)',
        borderBottom: `1px solid ${T.white10}`, background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(8px)',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px',
      }}>
        <a href="/" style={{ display: 'inline-flex', alignItems: 'center', textDecoration: 'none' }}>
          <img src={LOGO_CLARO} alt="LogiStock Pro"
            style={{ height: 'clamp(28px, 5vw, 36px)', width: 'auto', display: 'block' }}
            onError={(e) => { e.currentTarget.style.display = 'none'; e.currentTarget.nextSibling.style.display = 'inline-flex' }} />
          <span style={{ display: 'none', alignItems: 'center', gap: '10px' }}>
            <span style={{ width: '8px', height: '24px', background: T.yellow }} />
            <span style={{ fontSize: '15px', fontWeight: 800, letterSpacing: '-0.02em', textTransform: 'uppercase', color: T.white }}>LogiStock</span>
          </span>
        </a>
        <a href="/"
          style={{
            display: 'inline-flex', alignItems: 'center', gap: '6px',
            fontSize: '12px', fontWeight: 700, color: T.white70, textDecoration: 'none',
            padding: '8px 12px', borderRadius: '8px',
            transition: 'color 0.15s, background 0.15s',
          }}
          onMouseEnter={(e) => { e.currentTarget.style.color = T.yellow }}
          onMouseLeave={(e) => { e.currentTarget.style.color = T.white70 }}>
          <ArrowLeft size={14} /> <span style={{ letterSpacing: '0.08em', textTransform: 'uppercase' }}>Voltar</span>
        </a>
      </header>

      {/* Main */}
      <main style={{
        position: 'relative', zIndex: 1, flex: 1,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 'clamp(24px, 6vw, 48px) clamp(16px, 4vw, 32px)',
      }}>
        <div style={{ width: '100%', maxWidth: '480px' }}>

          {/* Eyebrow */}
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: '12px', marginBottom: '20px' }}>
            <span style={{ width: 'clamp(32px, 4vw, 44px)', height: '2px', background: T.yellow }} />
            <span style={{ fontSize: 'clamp(11px, 1.3vw, 13px)', letterSpacing: '0.24em', textTransform: 'uppercase', fontWeight: 700, color: T.white70 }}>
              {modo === 'login' && 'Acesso'}
              {modo === 'registo' && 'Novo registo'}
              {modo === 'registo_b2c' && 'Registo por convite'}
              {modo === 'recuperar' && 'Recuperar'}
              {modo === 'recuperar_enviado' && 'Email enviado'}
              {modo === 'definir_nova' && 'Nova password'}
              {modo === 'pendente_email' && 'Validar email'}
              {modo === 'pendente' && 'Submetido'}
            </span>
          </div>

          {/* ── LOGIN ── */}
          {modo === 'login' && (
            <Card>
              <h1 style={h1Style}>Iniciar sessão</h1>
              <p style={subStyle}>Introduz as tuas credenciais para aceder.</p>

              <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '16px', marginTop: '24px' }}>
                {error && <ErrorBox>{error}</ErrorBox>}
                <Field label="Email" icon={Mail}>
                  <input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="utilizador@empresa.com" autoComplete="email" style={inputStyle(email)} />
                  <EmailIcon value={email} />
                </Field>
                <Field label="Password" icon={Lock}>
                  <input type={showPass ? 'text' : 'password'} value={password} onChange={e => setPassword(e.target.value)} placeholder="••••••••" autoComplete="current-password" style={inputStyle()} />
                  <button type="button" onClick={() => setShowPass(p => !p)} style={iconBtnStyle}>
                    {showPass ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </Field>

                <button type="submit" disabled={loading} style={btnPrimary(loading)}>
                  {loading ? <><Loader size={16} style={{ animation: 'spin 1s linear infinite' }} /> A entrar...</> : <>Entrar <ArrowRight size={16} /></>}
                </button>

                <button type="button" onClick={() => { setModo('recuperar'); setRecEmail(email); setRecError('') }} style={textLinkRight}>
                  Esqueci-me da password
                </button>
              </form>

              <Divider />
              <button onClick={() => { setModo('registo'); setRError('') }} style={textLinkCenter}>
                <UserPlus size={14} /> Ainda não tens conta? <strong style={{ color: T.yellow, marginLeft: '4px' }}>Registar</strong>
              </button>
            </Card>
          )}

          {/* ── RECUPERAR ── */}
          {modo === 'recuperar' && (
            <Card>
              <h1 style={h1Style}><KeyRound size={24} color={T.yellow} style={{ verticalAlign: 'middle', marginRight: '10px' }} /> Recuperar password</h1>
              <p style={subStyle}>Enviamos-te um link para definires uma nova password.</p>

              <form onSubmit={handleRecuperar} style={{ display: 'flex', flexDirection: 'column', gap: '16px', marginTop: '24px' }}>
                {recError && <ErrorBox>{recError}</ErrorBox>}
                <Field label="Email da conta" icon={Mail}>
                  <input type="email" value={recEmail} onChange={e => setRecEmail(e.target.value)} placeholder="utilizador@empresa.com" autoComplete="email" style={inputStyle(recEmail)} />
                  <EmailIcon value={recEmail} />
                </Field>
                <button type="submit" disabled={recLoading} style={btnPrimary(recLoading)}>
                  {recLoading ? <><Loader size={16} style={{ animation: 'spin 1s linear infinite' }} /> A enviar...</> : <>Enviar link <ArrowRight size={16} /></>}
                </button>
              </form>

              <Divider />
              <button onClick={() => { setModo('login'); setRecError('') }} style={textLinkCenter}>
                <ArrowLeft size={14} /> Voltar ao login
              </button>
            </Card>
          )}

          {/* ── EMAIL DE RECUPERAÇÃO ENVIADO ── */}
          {modo === 'recuperar_enviado' && (
            <Card centered>
              <Mail size={56} color={T.yellow} style={{ marginBottom: '20px' }} />
              <h2 style={{ ...h1Style, fontSize: 'clamp(20px, 4vw, 28px)' }}>Email enviado</h2>
              <p style={{ ...subStyle, marginTop: '12px' }}>
                Se existir uma conta associada a <strong style={{ color: T.yellow }}>{recEmail}</strong>, recebes um email com um link para definires nova password.
                Verifica também o spam.
              </p>
              <button onClick={() => { setModo('login'); setRecError('') }} style={{ ...btnPrimary(false), marginTop: '24px', width: '100%' }}>
                <ArrowLeft size={16} /> Voltar ao login
              </button>
            </Card>
          )}

          {/* ── DEFINIR NOVA PASSWORD ── */}
          {modo === 'definir_nova' && (
            <Card>
              <h1 style={h1Style}><KeyRound size={24} color={T.yellow} style={{ verticalAlign: 'middle', marginRight: '10px' }} /> Nova password</h1>
              <p style={subStyle}>Define a nova password da tua conta.</p>

              <form onSubmit={handleDefinirNova} style={{ display: 'flex', flexDirection: 'column', gap: '16px', marginTop: '24px' }}>
                {novaError && <ErrorBox>{novaError}</ErrorBox>}
                {novaOk && (
                  <div style={successBoxStyle}>
                    <CheckCircle size={16} color={T.yellow} /> Password actualizada. A redireccionar...
                  </div>
                )}
                <Field label="Nova password" icon={Lock}>
                  <input type={novaPassShow ? 'text' : 'password'} value={novaPass} onChange={e => setNovaPass(e.target.value)} placeholder="Mínimo 8 caracteres" autoComplete="new-password" style={inputStyle()} />
                  <button type="button" onClick={() => setNovaPassShow(p => !p)} style={iconBtnStyle}>
                    {novaPassShow ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </Field>
                <Field label="Confirmar nova password" icon={Lock}>
                  <input type={novaPassShow ? 'text' : 'password'} value={novaPass2} onChange={e => setNovaPass2(e.target.value)} placeholder="Repete a password" autoComplete="new-password" style={inputStyle()} />
                </Field>
                <button type="submit" disabled={novaLoading || novaOk} style={btnPrimary(novaLoading || novaOk)}>
                  {novaLoading ? <><Loader size={16} style={{ animation: 'spin 1s linear infinite' }} /> A guardar...</> : 'Definir nova password'}
                </button>
              </form>
            </Card>
          )}

          {/* ── REGISTO ── */}
          {modo === 'registo' && (
            <Card>
              <h1 style={h1Style}>Criar conta</h1>
              <p style={subStyle}>O registo fica pendente até aprovação do administrador.</p>

              <form onSubmit={handleRegisto} style={{ display: 'flex', flexDirection: 'column', gap: '14px', marginTop: '24px' }}>
                {rError && <ErrorBox>{rError}</ErrorBox>}

                <Field label="Nome / Empresa *" icon={User}>
                  <input type="text" value={rNome} onChange={e => setRNome(e.target.value)} placeholder="Nome completo ou empresa" style={inputStyle()} />
                </Field>

                <Field label="Email *" icon={Mail}>
                  <input type="email" value={rEmail} onChange={e => setREmail(e.target.value)} placeholder="email@empresa.com" style={inputStyle(rEmail)} />
                  <EmailIcon value={rEmail} />
                </Field>
                <div style={infoBoxStyle}>
                  <AlertCircle size={14} color={T.yellow} style={{ flexShrink: 0, marginTop: '2px' }} />
                  <p style={{ margin: 0, fontSize: '12px', color: T.white70, lineHeight: 1.5 }}>
                    <strong style={{ color: T.yellow }}>Importante:</strong> usa o mesmo email da tua conta LOGNOW para garantir a sincronização correcta de stock e vendas.
                  </p>
                </div>

                <Field label="NIF *" icon={Hash}>
                  <input type="text" value={rNif}
                    onChange={e => { setRNif(e.target.value); setNifTipo(null) }}
                    onBlur={handleNifBlur}
                    placeholder="Ex: 123456789" maxLength={9}
                    style={{
                      ...inputStyle(),
                      paddingRight: '120px',
                      borderColor: nifTipo === 'ENI' ? T.success
                        : nifTipo === 'empresa' ? T.yellow
                        : nifTipo === 'invalido' ? T.danger
                        : T.white10,
                    }} />
                  <span style={{ position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)', fontSize: '11px', fontWeight: 700, display: 'flex', alignItems: 'center', gap: '4px' }}>
                    {nifVerificando && <><Loader size={11} style={{ animation: 'spin 1s linear infinite', color: T.white40 }} /> <span style={{ color: T.white40 }}>A verificar...</span></>}
                    {!nifVerificando && nifTipo === 'ENI' && <><CheckCircle size={12} color={T.success} /> <span style={{ color: T.success }}>ENI</span></>}
                    {!nifVerificando && nifTipo === 'empresa' && <><Building2 size={12} color={T.yellow} /> <span style={{ color: T.yellow }}>Empresa</span></>}
                    {!nifVerificando && nifTipo === 'invalido' && <><AlertCircle size={12} color={T.danger} /> <span style={{ color: T.danger }}>Inválido</span></>}
                    {!nifVerificando && (nifTipo === 'desconhecido' || nifTipo === 'particular') && <span style={{ color: T.white40 }}>Não verificado</span>}
                  </span>
                </Field>
                <p style={{ margin: '-6px 0 0', fontSize: '11px', color: T.white40 }}>
                  O NIF será verificado automaticamente.
                </p>

                <Field label="Telefone *" icon={Phone}>
                  <input type="tel" value={rTelefone} onChange={e => setRTelefone(e.target.value)} placeholder="+351 912 345 678" style={inputStyle()} />
                </Field>

                <Field label="Morada *" icon={MapPin}>
                  <input type="text" value={rMorada} onChange={e => setRMorada(e.target.value)} placeholder="Rua, nº, localidade, código postal" style={inputStyle()} />
                </Field>

                <Field label="Password *" icon={Lock}>
                  <input type={rShowPass ? 'text' : 'password'} value={rPass} onChange={e => setRPass(e.target.value)} placeholder="Mínimo 8 caracteres" style={inputStyle()} />
                  <button type="button" onClick={() => setRShowPass(p => !p)} style={iconBtnStyle}>
                    {rShowPass ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </Field>

                <button type="submit" disabled={rLoading} style={btnPrimary(rLoading)}>
                  {rLoading ? <><Loader size={16} style={{ animation: 'spin 1s linear infinite' }} /> A registar...</> : <><UserPlus size={16} /> Criar conta</>}
                </button>
              </form>

              <Divider />
              <button onClick={() => { setModo('login'); setRError('') }} style={textLinkCenter}>
                Já tens conta? <strong style={{ color: T.yellow, marginLeft: '4px' }}>Iniciar sessão</strong>
              </button>
            </Card>
          )}

          {/* ── REGISTO B2C (via link de convite) ── */}
          {modo === 'registo_b2c' && (
            <Card>
              <h1 style={h1Style}>Criar conta</h1>
              <p style={subStyle}>Foste convidado para a plataforma LogicPro. Preenche os teus dados.</p>

              {conviteEstado === 'a_validar' && (
                <div style={{ ...infoBoxStyle, marginTop: '20px' }}>
                  <Loader size={14} style={{ animation: 'spin 1s linear infinite', color: T.yellow, flexShrink: 0, marginTop: '2px' }} />
                  <p style={{ margin: 0, fontSize: '13px', color: T.white70 }}>A validar o teu link de convite...</p>
                </div>
              )}

              {conviteEstado && typeof conviteEstado === 'object' && conviteEstado.erro && (
                <div style={{ marginTop: '20px' }}>
                  <ErrorBox>
                    {conviteEstado.erro === 'ja_usado' && 'Este link de convite já foi utilizado.'}
                    {conviteEstado.erro === 'expirado' && 'Este link de convite expirou. Pede um novo ao administrador.'}
                    {conviteEstado.erro === 'invalido' && 'Link de convite inválido. Verifica que copiaste o URL completo.'}
                  </ErrorBox>
                  <button onClick={() => setModo('login')} style={{ ...btnPrimary(false), marginTop: '16px', width: '100%' }}>
                    <ArrowLeft size={16} /> Voltar ao login
                  </button>
                </div>
              )}

              {conviteEstado === 'valido' && (
                <form onSubmit={handleRegistoB2C} style={{ display: 'flex', flexDirection: 'column', gap: '14px', marginTop: '24px' }}>
                  {rError && <ErrorBox>{rError}</ErrorBox>}

                  <Field label="Nome completo *" icon={User}>
                    <input type="text" value={rNome} onChange={e => setRNome(e.target.value)} placeholder="O teu nome" style={inputStyle()} />
                  </Field>

                  <Field label="Email *" icon={Mail}>
                    <input type="email" value={rEmail} onChange={e => setREmail(e.target.value)} placeholder="email@dominio.com" style={inputStyle(rEmail)} />
                    <EmailIcon value={rEmail} />
                  </Field>

                  <Field label="NIF (opcional)" icon={Hash}>
                    <input type="text" value={rNif} onChange={e => setRNif(e.target.value)} placeholder="Ex: 123456789 (para factura)" maxLength={9} style={inputStyle()} />
                  </Field>

                  <Field label="Telefone (opcional)" icon={Phone}>
                    <input type="tel" value={rTelefone} onChange={e => setRTelefone(e.target.value)} placeholder="+351 912 345 678" style={inputStyle()} />
                  </Field>

                  <Field label="Morada (opcional)" icon={MapPin}>
                    <input type="text" value={rMorada} onChange={e => setRMorada(e.target.value)} placeholder="Para envio das encomendas" style={inputStyle()} />
                  </Field>

                  <Field label="Password *" icon={Lock}>
                    <input type={rShowPass ? 'text' : 'password'} value={rPass} onChange={e => setRPass(e.target.value)} placeholder="Mínimo 8 caracteres" style={inputStyle()} />
                    <button type="button" onClick={() => setRShowPass(p => !p)} style={iconBtnStyle}>
                      {rShowPass ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  </Field>

                  <button type="submit" disabled={rLoading} style={btnPrimary(rLoading)}>
                    {rLoading ? <><Loader size={16} style={{ animation: 'spin 1s linear infinite' }} /> A registar...</> : <><UserPlus size={16} /> Criar conta</>}
                  </button>
                </form>
              )}
            </Card>
          )}

          {/* ── PENDENTE EMAIL ── */}
          {modo === 'pendente_email' && (
            <Card centered>
              <Mail size={56} color={T.yellow} style={{ marginBottom: '20px' }} />
              <h2 style={{ ...h1Style, fontSize: 'clamp(20px, 4vw, 28px)' }}>Valida o teu email</h2>
              <p style={{ ...subStyle, marginTop: '12px' }}>
                Enviámos um email de confirmação para <strong style={{ color: T.yellow }}>{rEmail}</strong>. Clica no link no email para validares a tua conta.
              </p>
              <div style={{ ...infoBoxStyle, marginTop: '20px', justifyContent: 'center' }}>
                <span style={{ fontSize: '18px' }}>⏳</span>
                <p style={{ margin: 0, fontSize: '12px', color: T.white70, lineHeight: 1.5, textAlign: 'left' }}>
                  Após validares o email, o administrador será notificado para aprovar o teu registo.
                </p>
              </div>
              <button onClick={() => { setModo('login'); setRError('') }} style={{ ...btnPrimary(false), marginTop: '24px', width: '100%' }}>
                <ArrowLeft size={16} /> Voltar ao login
              </button>
            </Card>
          )}

          {/* ── PENDENTE (legado) ── */}
          {modo === 'pendente' && (
            <Card centered>
              <CheckCircle size={56} color={T.success} style={{ marginBottom: '20px' }} />
              <h2 style={{ ...h1Style, fontSize: 'clamp(20px, 4vw, 28px)' }}>Registo submetido</h2>
              <p style={{ ...subStyle, marginTop: '12px' }}>
                A tua conta foi criada e está <strong style={{ color: T.yellow }}>pendente de aprovação</strong> pelo administrador. Serás notificado por email quando a conta for activada.
              </p>
              <button onClick={() => setModo('login')} style={{ ...btnPrimary(false), marginTop: '24px', width: '100%' }}>
                <ArrowLeft size={16} /> Voltar ao login
              </button>
            </Card>
          )}

          {/* Footer line discreto + botao instalar app */}
          <div style={{
            textAlign: 'center', marginTop: '32px',
            display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '12px',
          }}>
            <p style={{
              margin: 0, fontSize: '11px', color: T.white40,
              fontFamily: 'ui-monospace, monospace', letterSpacing: '0.18em', textTransform: 'uppercase',
            }}>
              <span style={{ color: T.yellow }}>//</span> LogiStock Pro · acesso seguro
            </p>
            <PwaInstallBanner />
          </div>
        </div>
      </main>

      <style>{`
        @keyframes spin { from { transform: rotate(0); } to { transform: rotate(360deg); } }
        input:focus { outline: 2px solid ${T.yellow} !important; outline-offset: 1px; border-color: ${T.yellow} !important; }
        input::placeholder { color: ${T.white40}; }
        button:disabled { cursor: not-allowed !important; }
        html { scroll-behavior: smooth; }
        body, #root { overflow-x: hidden !important; }
        @media (max-width: 480px) {
          .login-card-inner { padding: 24px 20px !important; }
        }
      `}</style>
    </div>
  )
}

// ─────────── Estilos partilhados ───────────

const h1Style = {
  fontSize: 'clamp(24px, 4.5vw, 36px)', fontWeight: 800, letterSpacing: '-0.03em',
  lineHeight: 1.05, margin: 0, color: T.white,
}

const subStyle = {
  fontSize: 'clamp(14px, 1.8vw, 16px)', color: T.white70, lineHeight: 1.55, margin: '8px 0 0',
}

const labelStyle = {
  display: 'block', fontSize: '11px', fontWeight: 800, color: T.white70,
  marginBottom: '8px', letterSpacing: '0.18em', textTransform: 'uppercase',
}

const inputStyle = (emailValue = null) => {
  let borderColor = T.white10
  if (emailValue !== null && emailValue !== '') {
    const status = validarEmail(emailValue)
    if (status === 'valid') borderColor = T.success
    else if (status === 'invalid') borderColor = T.danger
  }
  return {
    width: '100%', height: '52px', padding: '0 14px 0 42px',
    border: `1.5px solid ${borderColor}`, borderRadius: '10px',
    fontSize: '15px', color: T.white, fontWeight: 500,
    background: T.zinc900, outline: 'none', boxSizing: 'border-box',
    fontFamily: 'inherit', transition: 'border-color 0.15s',
  }
}

const btnPrimary = (disabled) => ({
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '10px',
  padding: '0 24px', minHeight: '52px', width: '100%',
  background: T.yellow, color: T.black, border: 'none', borderRadius: '10px',
  fontSize: 'clamp(12px, 1.4vw, 14px)', fontWeight: 800, letterSpacing: '0.14em', textTransform: 'uppercase',
  cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.6 : 1,
  transition: 'transform 0.15s, background 0.15s', marginTop: '4px',
})

const iconBtnStyle = {
  position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)',
  background: 'transparent', border: 'none', cursor: 'pointer', color: T.white40, padding: '4px',
  display: 'inline-flex', alignItems: 'center', transition: 'color 0.15s',
}

const textLinkRight = {
  background: 'transparent', border: 'none', cursor: 'pointer', color: T.white70,
  fontSize: '12px', fontWeight: 600, padding: '0', textAlign: 'right',
  alignSelf: 'flex-end', marginTop: '-8px', letterSpacing: '0.06em',
  textDecoration: 'underline', transition: 'color 0.15s',
}

const textLinkCenter = {
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '6px',
  width: '100%', background: 'transparent', border: 'none', cursor: 'pointer',
  color: T.white70, fontSize: '13px', fontWeight: 600, padding: '12px 0',
  letterSpacing: '0.04em', transition: 'color 0.15s',
}

// ─────────── Componentes auxiliares ───────────

function Card({ children, centered }) {
  return (
    <div className="login-card-inner" style={{
      background: T.zinc950, border: `1px solid ${T.white10}`, borderRadius: '20px',
      padding: 'clamp(28px, 5vw, 40px)',
      boxShadow: '0 24px 64px rgba(0,0,0,0.5)',
      textAlign: centered ? 'center' : 'left',
    }}>
      {children}
    </div>
  )
}

function Divider() {
  return (
    <div style={{ height: '1px', background: T.white10, margin: '24px 0 4px' }} />
  )
}

function Field({ label, icon: Icon, children }) {
  return (
    <div>
      <label style={labelStyle}>{label}</label>
      <div style={{ position: 'relative' }}>
        {Icon && <Icon size={16} style={{ position: 'absolute', left: '14px', top: '50%', transform: 'translateY(-50%)', color: T.white40, pointerEvents: 'none' }} />}
        {children}
      </div>
    </div>
  )
}

function EmailIcon({ value }) {
  const status = validarEmail(value)
  if (status === 'empty') return null
  const isValid = status === 'valid'
  return (
    <span style={{ position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)', display: 'flex', color: isValid ? T.success : T.danger }}>
      {isValid ? <CheckCircle size={16} /> : <AlertCircle size={16} />}
    </span>
  )
}

function ErrorBox({ children }) {
  return (
    <div style={{
      display: 'flex', alignItems: 'flex-start', gap: '10px',
      padding: '12px 14px', background: 'rgba(239,68,68,0.10)', border: `1px solid ${T.danger}`, borderRadius: '8px',
      color: T.white, fontSize: '13px', lineHeight: 1.5,
    }}>
      <AlertCircle size={16} color={T.danger} style={{ flexShrink: 0, marginTop: '1px' }} />
      <span>{children}</span>
    </div>
  )
}

const successBoxStyle = {
  display: 'flex', alignItems: 'center', gap: '10px',
  padding: '12px 14px', background: 'rgba(250,204,21,0.10)', border: `1px solid ${T.yellow}`, borderRadius: '8px',
  color: T.yellow, fontSize: '13px', fontWeight: 600,
}

const infoBoxStyle = {
  display: 'flex', alignItems: 'flex-start', gap: '10px',
  padding: '12px 14px', background: 'rgba(250,204,21,0.06)', border: `1px solid ${T.yellow}33`, borderRadius: '8px',
}

const dangerBoxStyle = {
  display: 'flex', alignItems: 'flex-start', gap: '10px',
  padding: '12px 14px', background: 'rgba(239,68,68,0.10)', border: `1px solid ${T.danger}`, borderRadius: '8px',
}
